import React, { useState, useEffect } from 'react';
import { useRegistrosLimpieza, useUsuariosActivos } from '../hooks/useRegistrosLimpieza';
import { RegistroLimpieza, ActualizarRegistroLimpieza } from '../types';
import ImageGallery from '../components/ImageGallery';
import DateRangeFilter, { DateFilterOptions } from '../components/DateRangeFilter';
import { getEffectiveDateRange } from '../utils/dateFilterUtils';
import { imagenRegistroService, ImagenRegistro } from '../services/imagenRegistroService';

const Registros: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isChangingPage, setIsChangingPage] = useState(false);
  
  // Verificar si hay filtros activos
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterUsuarioId, setFilterUsuarioId] = useState<number | 'todos'>('todos');
  const [dateFilter, setDateFilter] = useState<DateFilterOptions>({
    mode: 'preset',
    preset: 'todos'
  });
  
  // Preparar parámetros de filtro para el backend
  const dateRange = getEffectiveDateRange(dateFilter);
  const fechaInicio = dateRange.startDate;
  const fechaFin = dateRange.endDate;
  
  const {
    registrosLimpieza,
    registrosLimpiezaPaginated,
    isLoading,
    error,
    updateRegistro,
    deleteRegistro,
    isUpdating,
    isDeleting,
  } = useRegistrosLimpieza(
    currentPage, 
    pageSize,
    searchTerm || undefined,
    filterUsuarioId !== 'todos' ? filterUsuarioId : undefined,
    filterEstado !== 'todos' ? filterEstado : undefined,
    fechaInicio,
    fechaFin
  );
  
  const { data: usuariosActivos } = useUsuariosActivos();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedRegistroId, setExpandedRegistroId] = useState<number | null>(null);
  const [imagenesByRegistro, setImagenesByRegistro] = useState<Record<number, ImagenRegistro[]>>({});

  const loadImagenes = async (registroId: number) => {
    try {
      const imgs = await imagenRegistroService.getByRegistroLimpieza(registroId);
      setImagenesByRegistro(prev => ({ ...prev, [registroId]: imgs }));
    } catch (e) {
    }
  };

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Resetear indicador de carga cuando cambien los datos
  useEffect(() => {
    if (registrosLimpiezaPaginated) {
      setIsChangingPage(false);
    }
  }, [registrosLimpiezaPaginated]);
  // Los filtros ya se aplican en el backend, solo usamos los registros directamente
  const registrosConFiltroFecha = registrosLimpieza;

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterEstado, filterUsuarioId, dateFilter.mode, dateFilter.preset, dateFilter.date, dateFilter.startDate, dateFilter.endDate]);


  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este registro? Esta acción no se puede deshacer.')) {
      try {
        await deleteRegistro(id);
        setSuccessMessage('Registro eliminado exitosamente');
        setShowSuccessMessage(true);
        // El hook ya invalida las queries automáticamente, 
        // el useEffect de auto-ajuste se encargará de recargar si es necesario
      } catch (error) {
      }
    }
  };

  const handleImageDeleted = (imagenId: number) => {
    setSuccessMessage('Imagen eliminada exitosamente');
    setShowSuccessMessage(true);
    // Refrescar listado de imágenes del registro expandido
    if (expandedRegistroId) {
      setImagenesByRegistro(prev => ({
        ...prev,
        [expandedRegistroId]: (prev[expandedRegistroId] || []).filter(img => img.id !== imagenId)
      }));
    }
  };

  // Funciones para manejar paginación
  const handlePageChange = (newPage: number) => {
    setIsChangingPage(true);
    setCurrentPage(newPage);
    // Scroll hacia arriba para mejor UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setIsChangingPage(true);
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset a la primera página
    // Scroll hacia arriba para mejor UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFinalizar = async (registro: RegistroLimpieza) => {
    try {
      const updateData: ActualizarRegistroLimpieza = {
        fechaHoraFin: new Date().toISOString(),
        observaciones: registro.observaciones,
        detallesTareas: registro.detallesTareas.map(detalle => ({
          id: detalle.id,
          realizada: true,
          fechaHoraRealizacion: new Date().toISOString(),
        })),
      };
      await updateRegistro({ id: registro.id, registro: updateData });
      setSuccessMessage('Registro finalizado exitosamente');
      setShowSuccessMessage(true);
    } catch (error) {
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstadoRegistro = (registro: RegistroLimpieza) => {
    return registro.fechaHoraFin ? 'Completado' : 'En Proceso';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando registros...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error al cargar registros</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error.message || 'Ha ocurrido un error inesperado. Por favor, intenta de nuevo.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 lg:px-8">
      <div className="py-6">
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}



        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900">Registros de Limpieza</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Gestiona los registros de limpieza de las áreas
              <span className="ml-2 text-blue-600 font-medium">• Ordenados por fecha de inicio (más reciente primero)</span>
            </p>
          </div>
          
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Buscar por área, usuario o observaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Usuario
              </label>
              <select
                value={filterUsuarioId}
                onChange={(e) => setFilterUsuarioId(e.target.value === 'todos' ? 'todos' : parseInt(e.target.value))}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todos los usuarios</option>
                {usuariosActivos?.data?.map((u: any) => (
                  <option key={u.id} value={u.id}>{u.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Estado
              </label>
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todos los registros</option>
                <option value="en-proceso">En proceso</option>
                <option value="completados">Completados</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs sm:text-sm text-gray-600">
                {registrosConFiltroFecha.length} registro{registrosConFiltroFecha.length !== 1 ? 's' : ''} encontrado{registrosConFiltroFecha.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Filtro de fechas */}
        <DateRangeFilter
          value={dateFilter}
          onChange={setDateFilter}
          className="mb-6"
        />


        {/* Creación/edición interna deshabilitada: los registros se gestionan desde QR público */}

        {/* Modal de creación interno deshabilitado */}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {registrosConFiltroFecha.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm || filterEstado !== 'todos' || filterUsuarioId !== 'todos'
                  ? 'No se encontraron registros que coincidan con los filtros.' 
                  : 'No hay registros disponibles.'}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {registrosConFiltroFecha.map((registro) => (
                <li key={registro.id}>
                  <div className="px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                            registro.fechaHoraFin ? 'bg-green-500' : 'bg-orange-500'
                          }`}>
                            <span className="text-white text-sm font-medium">
                              {registro.areaNombre.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <div className="text-sm sm:text-base font-medium text-gray-900">
                            {registro.areaNombre}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500">
                            Usuario: {registro.usuarioNombre}
                          </div>
                          {registro.observaciones && (
                            <div className="text-xs sm:text-sm text-gray-500">
                              {registro.observaciones}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 sm:gap-3 mt-1">
                            <div className={`text-xs sm:text-sm px-2 py-1 rounded-full ${
                              registro.fechaHoraFin 
                                ? 'text-green-600 bg-green-100' 
                                : 'text-orange-600 bg-orange-100'
                            }`}>
                              {getEstadoRegistro(registro)}
                            </div>
                            <div className="text-xs sm:text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              {registro.detallesTareas.length} tarea{registro.detallesTareas.length !== 1 ? 's' : ''}
                            </div>
                            <div className="text-xs sm:text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                              {registro.detallesTareas.filter(t => t.realizada).length} completada{registro.detallesTareas.filter(t => t.realizada).length !== 1 ? 's' : ''}
                            </div>
                            {/* Eficiencia individual removida - solo se muestra eficiencia promedio global */}
                            {registro.tiempoEstimadoMinutos && (
                              <div className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                Estimado: {registro.tiempoEstimadoMinutos}min
                              </div>
                            )}
                            {registro.tiempoRealMinutos && (
                              <div className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                Real: {registro.tiempoRealMinutos}min
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:items-end w-full sm:w-auto">
                        <div className="text-xs sm:text-sm text-gray-500">
                          Inicio: {formatDateTime(registro.fechaHoraInicio)}
                        </div>
                        {registro.fechaHoraFin && (
                          <div className="text-xs sm:text-sm text-gray-500">
                            Fin: {formatDateTime(registro.fechaHoraFin)}
                          </div>
                        )}
                        <div className="flex flex-col sm:flex-row gap-2 mt-2 w-full sm:w-auto">
                          {!registro.fechaHoraFin && (
                            <button
                              onClick={() => handleFinalizar(registro)}
                              disabled={isUpdating}
                              className="text-green-600 hover:text-green-900 text-sm font-medium disabled:opacity-50 py-3 px-3 w-full sm:w-auto text-center rounded-md"
                            >
                              Finalizar
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setExpandedRegistroId(prev => {
                                const next = prev === registro.id ? null : registro.id;
                                if (next === registro.id && !imagenesByRegistro[registro.id]) {
                                  loadImagenes(registro.id);
                                }
                                return next;
                              });
                            }}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md"
                          >
                            {expandedRegistroId === registro.id ? 'Ocultar detalle' : 'Ver detalle'}
                          </button>
                          <button
                            onClick={() => handleDelete(registro.id)}
                            disabled={isDeleting}
                            className="text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50 py-3 px-3 w-full sm:w-auto text-center rounded-md"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  

                  
                  {expandedRegistroId === registro.id && (
                    <div className="px-3 sm:px-4 pb-4">
                      {registro.observaciones && (
                        <div className="mb-3 text-xs sm:text-sm text-gray-700">
                          <span className="font-medium">Observaciones:</span> {registro.observaciones}
                        </div>
                      )}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Tareas de Limpieza</h4>
                        {registro.detallesTareas.length === 0 ? (
                          <div className="text-xs sm:text-sm text-gray-500">No hay detalles de tareas.</div>
                        ) : (
                          <div className="space-y-2">
                            {registro.detallesTareas.map((dt) => (
                              <div key={dt.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-gray-50 rounded-md">
                                <div>
                                  <div className="text-xs sm:text-sm font-medium text-gray-900">{dt.tareaNombre}</div>
                                  {dt.tareaDescripcion && (
                                    <div className="text-xs sm:text-sm text-gray-500">{dt.tareaDescripcion}</div>
                                  )}
                                  {!dt.realizada && dt.motivoNoRealizada && (
                                    <div className="text-xs sm:text-sm text-red-600">Motivo: {dt.motivoNoRealizada}</div>
                                  )}
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${dt.realizada ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                  {dt.realizada ? 'Realizada' : 'Pendiente'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Galería de imágenes */}
                      <div className="mt-4">
                        <ImageGallery
                          imagenes={imagenesByRegistro[registro.id] || registro.imagenes || []}
                          onImageDeleted={handleImageDeleted}
                          onError={(message) => setSuccessMessage(message)}
                        />
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* 🔥 CONTROLES DE PAGINACIÓN MEJORADOS */}
          {registrosLimpiezaPaginated && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-lg shadow-sm border">
              <div className="text-sm text-gray-700">
                {isChangingPage ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Cambiando página...
                  </span>
                ) : (
                  `Mostrando ${((currentPage - 1) * pageSize) + 1} a ${Math.min(currentPage * pageSize, registrosLimpiezaPaginated.totalCount)} de ${registrosLimpiezaPaginated.totalCount} registros`
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </select>
                
                <div className="flex items-center gap-1">
                  {/* Botón Primera Página */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || isChangingPage}
                    className="px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Primera página"
                  >
                    ««
                  </button>
                  
                  {/* Botón Anterior */}
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={!registrosLimpiezaPaginated.hasPreviousPage || isChangingPage}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  
                  {/* Navegación de páginas */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, registrosLimpiezaPaginated.totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (registrosLimpiezaPaginated.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= registrosLimpiezaPaginated.totalPages - 2) {
                        pageNum = registrosLimpiezaPaginated.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      if (pageNum > 0 && pageNum <= registrosLimpiezaPaginated.totalPages) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={isChangingPage}
                            className={`px-3 py-2 text-sm font-medium rounded-md ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white border border-blue-600'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  {/* Botón Siguiente */}
                  <button
                    onClick={() => handlePageChange(Math.min(registrosLimpiezaPaginated.totalPages, currentPage + 1))}
                    disabled={!registrosLimpiezaPaginated.hasNextPage || isChangingPage}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                  
                  {/* Botón Última Página */}
                  <button
                    onClick={() => handlePageChange(registrosLimpiezaPaginated.totalPages)}
                    disabled={currentPage === registrosLimpiezaPaginated.totalPages || isChangingPage}
                    className="px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Última página"
                  >
                    »»
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Registros; 